export interface OutputMediaItem {
  type: "output_media";
  media_type?: string;
  url?: string;
  storage_key?: string;
  title?: string;
  mime_type?: string;
  [key: string]: unknown;
}

export interface TextContentItem {
  type: "text";
  text: string;
}

export interface JsonContentItem {
  type: "json";
  value: unknown;
}

export type MultiContentItem = OutputMediaItem | TextContentItem | JsonContentItem;

export interface MultiContentValue {
  type: "multi_content";
  items: MultiContentItem[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

export const isOutputMediaItem = (value: unknown): value is OutputMediaItem =>
  isRecord(value) && value.type === "output_media";

export const isOutputMediaItemArray = (value: unknown): value is OutputMediaItem[] =>
  Array.isArray(value) && value.length > 0 && value.every(isOutputMediaItem);

const isTextContentItem = (value: unknown): value is TextContentItem =>
  isRecord(value) && value.type === "text" && typeof value.text === "string";

const isJsonContentItem = (value: unknown): value is JsonContentItem =>
  isRecord(value) && value.type === "json" && "value" in value;

export const isMultiContentItem = (value: unknown): value is MultiContentItem =>
  isOutputMediaItem(value) || isTextContentItem(value) || isJsonContentItem(value);

export const isMultiContentValue = (value: unknown): value is MultiContentValue =>
  isRecord(value) &&
  value.type === "multi_content" &&
  Array.isArray(value.items) &&
  value.items.every(isMultiContentItem);

export type MediaKind = "image" | "video" | "audio" | "pdf" | "file";

export const getMediaKind = (mediaType?: string): MediaKind => {
  if (!mediaType) {
    return "file";
  }

  if (mediaType.startsWith("image")) {
    return "image";
  }

  if (mediaType.startsWith("video")) {
    return "video";
  }

  if (mediaType.startsWith("audio")) {
    return "audio";
  }

  if (mediaType === "pdf" || mediaType === "application/pdf") {
    return "pdf";
  }

  return "file";
};

const getFilenameFromPath = (path: string | undefined): string | null => {
  if (!path || path.startsWith("data:")) {
    return null;
  }

  const withoutQuery = path.split("?")[0] ?? path;
  const parts = withoutQuery.split("/").filter(Boolean);
  const filename = parts[parts.length - 1];
  return filename && filename.length > 0 ? decodeURIComponent(filename) : null;
};

export const getMediaLabel = (item: OutputMediaItem, fallbackIndex?: number): string => {
  const explicitTitle = item.title?.trim();
  if (explicitTitle) {
    return explicitTitle;
  }

  const filename =
    getFilenameFromPath(item.storage_key) ??
    getFilenameFromPath(item.url) ??
    (typeof item.filename === "string" ? item.filename : null);
  if (filename) {
    return filename;
  }

  const kind = getMediaKind(item.media_type);
  if (fallbackIndex !== undefined) {
    return `${kind.charAt(0).toUpperCase()}${kind.slice(1)} ${fallbackIndex + 1}`;
  }

  return `${kind.charAt(0).toUpperCase()}${kind.slice(1)} file`;
};

export const summarizeMultiContent = (value: MultiContentValue): string => {
  const textCount = value.items.filter(item => item.type === "text").length;
  const mediaCount = value.items.filter(item => item.type === "output_media").length;
  const jsonCount = value.items.filter(item => item.type === "json").length;

  if (textCount > 0 && mediaCount > 0) {
    return mediaCount === 1 ? "Text + 1 file" : `Text + ${mediaCount} files`;
  }

  if (jsonCount > 0 && mediaCount > 0) {
    return mediaCount === 1 ? "Data + 1 file" : `Data + ${mediaCount} files`;
  }

  if (mediaCount > 0) {
    return mediaCount === 1 ? "1 file" : `${mediaCount} files`;
  }

  if (textCount > 0) {
    return textCount === 1 ? "Text" : `${textCount} text blocks`;
  }

  if (jsonCount > 0) {
    return jsonCount === 1 ? "Data" : `${jsonCount} data blocks`;
  }

  return "Mixed content";
};
